// ============================================================
// textMetrics.js — measure / wrap / fit text for the drawing sheets
//
// The sheets are built as SVG and then written into the PDF with svg2pdf,
// which sets text in the PDF's built-in Helvetica. Measuring with jsPDF's
// own Helvetica metrics means what we lay out is exactly what prints
// (no text running past a table cell or the title block).
// ============================================================

import jsPDF from 'jspdf';

let meter = null;
const getMeter = () => {
  if (!meter) meter = new jsPDF({ unit: 'pt' });
  return meter;
};

// Width in points of `text` at `size` pt (bold optional)
export function textWidth(text, size, bold = false) {
  const m = getMeter();
  m.setFont('helvetica', bold ? 'bold' : 'normal');
  return m.getStringUnitWidth(String(text ?? '')) * size;
}

// Trims with an ellipsis so the text fits `maxW` points
export function fitText(text, size, maxW, bold = false) {
  let s = String(text ?? '');
  if (textWidth(s, size, bold) <= maxW) return s;
  while (s.length > 1 && textWidth(s + '…', size, bold) > maxW) s = s.slice(0, -1);
  return s + '…';
}

// Greedy word wrap into lines no wider than `maxW` points
export function wrapText(text, size, maxW, bold = false) {
  const out = [];
  String(text ?? '').split(/\n/).forEach((para) => {
    const words = para.split(/\s+/).filter(Boolean);
    let line = '';
    words.forEach((w) => {
      const tryLine = line ? `${line} ${w}` : w;
      if (textWidth(tryLine, size, bold) <= maxW || !line) line = tryLine;
      else { out.push(line); line = w; }
    });
    out.push(line);
  });
  return out;
}

// Escapes text for safe use inside SVG markup
export const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// Feet (decimal) → architectural feet-inches, e.g. 3.17 → 3'-2"
export function ftIn(ft) {
  const neg = ft < 0;
  let inches = Math.round(Math.abs(ft) * 12);
  const f = Math.floor(inches / 12);
  inches -= f * 12;
  return `${neg ? '-' : ''}${f}'-${inches}"`;
}
